import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import userSettingsService from '../services/userSettingsService';
import { trackEvent } from '../utils/analytics';
import './LanguageSwitcher.css';

const languages = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', addressField: 'address' },
  { code: 'en', name: 'English', flag: '🇬🇧', addressField: 'addressEn' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', addressField: 'addressRu' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', addressField: 'addressDe' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', addressField: 'addressAr' },
];

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, updateUserLanguage } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLangCode, setCurrentLangCode] = useState(i18n.language);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(lang => lang.code === currentLangCode) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync local state with i18n language changes (e.g., after login)
  useEffect(() => {
    setCurrentLangCode(i18n.language);
  }, [i18n.language]);

  const handleLanguageSelect = (langCode) => {
    if (langCode === currentLangCode) {
      setIsOpen(false);
      return;
    }

    trackEvent('language_changed', {
      from_language: currentLangCode,
      to_language: langCode
    });

    // Update local state immediately for instant UI feedback
    setCurrentLangCode(langCode);
    setIsOpen(false);

    // Update i18n and localStorage
    i18n.changeLanguage(langCode);

    // Save to localStorage with error handling (for incognito mode, etc.)
    try {
      localStorage.setItem('language', langCode);
    } catch (error) {
      console.warn('Failed to save language to localStorage:', error);
    }

    // Update user state and save to DB if authenticated
    if (isAuthenticated) {
      updateUserLanguage(langCode);
      userSettingsService.updateLanguage(langCode).catch(error => {
        console.error('Failed to save language preference:', error);
      });
    }
  };

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <button
        className="language-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('common.selectLanguage')}
      >
        <span className="language-code">{currentLang.code.toUpperCase()}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="language-dropdown">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${lang.code === currentLangCode ? 'active' : ''}`}
              onClick={() => handleLanguageSelect(lang.code)}
            >
              <span className="flag-icon">{lang.flag}</span>
              <span className="language-name">{lang.name}</span>
              {lang.code === currentLangCode && <span className="checkmark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
export { languages };
